const DB_NAME = "aiclotheschanger-db";
const DB_VERSION = 1;
const STORE_NAME = "project_images";
const CACHED_AT_INDEX = "cachedAt";
const MAX_ROWS = 60;

export interface CachedProjectImage {
  requestId: string;
  blob: Blob;
  cachedAt: number;
  outputUrl: string;
}

let dbPromise: Promise<IDBDatabase | null> | null = null;
const inflight = new Map<string, Promise<{ blob: Blob; outputUrl: string }>>();

function openDb(): Promise<IDBDatabase | null> {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise<IDBDatabase | null>(resolve => {
    if (typeof indexedDB === "undefined") {
      resolve(null);
      return;
    }
    let request: IDBOpenDBRequest;
    try {
      request = indexedDB.open(DB_NAME, DB_VERSION);
    } catch (error) {
      console.warn("[projectImageCache] indexedDB.open threw:", error);
      resolve(null);
      return;
    }

    request.onupgradeneeded = () => {
      const db = request.result;
      if (!db.objectStoreNames.contains(STORE_NAME)) {
        const store = db.createObjectStore(STORE_NAME, { keyPath: "requestId" });
        store.createIndex(CACHED_AT_INDEX, "cachedAt", { unique: false });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => {
      console.warn("[projectImageCache] indexedDB.open error:", request.error);
      resolve(null);
    };
    request.onblocked = () => {
      console.warn("[projectImageCache] indexedDB.open blocked");
      resolve(null);
    };
  });
  return dbPromise;
}

function tx(
  db: IDBDatabase,
  mode: IDBTransactionMode,
): { store: IDBObjectStore; done: Promise<void> } {
  const transaction = db.transaction(STORE_NAME, mode);
  const store = transaction.objectStore(STORE_NAME);
  const done = new Promise<void>((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
    transaction.onabort = () => reject(transaction.error);
  });
  return { store, done };
}

async function readEntry(requestId: string): Promise<CachedProjectImage | null> {
  const db = await openDb();
  if (!db) return null;
  try {
    const { store } = tx(db, "readonly");
    return await new Promise<CachedProjectImage | null>((resolve, reject) => {
      const req = store.get(requestId);
      req.onsuccess = () => {
        const value = req.result;
        if (
          value &&
          typeof value === "object" &&
          "blob" in value &&
          value.blob instanceof Blob
        ) {
          resolve(value as CachedProjectImage);
        } else {
          resolve(null);
        }
      };
      req.onerror = () => reject(req.error);
    });
  } catch (error) {
    console.warn("[projectImageCache] readEntry failed:", error);
    return null;
  }
}

async function writeEntry(entry: CachedProjectImage): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const { store, done } = tx(db, "readwrite");
    store.put(entry);
    await done;
    void enforceLruCap();
  } catch (error: unknown) {
    // QuotaExceededError can come through as DOMException name "QuotaExceededError"
    // or as a generic write failure. Either way we silently skip caching — the
    // caller still has a working blob URL from the in-memory blob.
    if (
      error instanceof DOMException &&
      (error.name === "QuotaExceededError" || error.code === 22)
    ) {
      console.warn("[projectImageCache] quota exceeded, skipping cache write");
      return;
    }
    console.warn("[projectImageCache] writeEntry failed:", error);
  }
}

async function enforceLruCap(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  // First read the count in its own transaction; if we're under the cap, bail.
  // Doing the count in the same readwrite txn as the cursor walk lets the txn
  // auto-commit before the cursor is opened (TransactionInactiveError),
  // because openCursor was scheduled from inside the count's onsuccess.
  let count = 0;
  try {
    const { store, done } = tx(db, "readonly");
    const req = store.count();
    await new Promise<void>(resolve => {
      req.onsuccess = () => {
        count = req.result;
        resolve();
      };
      req.onerror = () => resolve();
    });
    await done;
  } catch (error) {
    console.warn("[projectImageCache] enforceLruCap count failed:", error);
    return;
  }
  if (count <= MAX_ROWS) return;
  const toRemove = count - MAX_ROWS;

  try {
    const { store, done } = tx(db, "readwrite");
    const index = store.index(CACHED_AT_INDEX);
    let removed = 0;
    const cursorReq = index.openCursor();
    cursorReq.onsuccess = () => {
      const cursor = cursorReq.result;
      if (!cursor || removed >= toRemove) return;
      cursor.delete();
      removed += 1;
      cursor.continue();
    };
    cursorReq.onerror = () =>
      console.warn("[projectImageCache] LRU cursor failed:", cursorReq.error);
    await done;
  } catch (error) {
    console.warn("[projectImageCache] enforceLruCap evict failed:", error);
  }
}

async function fetchBlob(url: string): Promise<Blob> {
  const response = await fetch(url, { mode: "cors" });
  if (!response.ok) throw new Error(`HTTP ${response.status}`);
  return response.blob();
}

async function fetchWithFallbacks(
  primary: string,
  fallbacks: string[],
): Promise<{ blob: Blob; outputUrl: string }> {
  const candidates = [primary, ...fallbacks.filter(u => u && u !== primary)];
  let lastError: unknown = null;
  for (const url of candidates) {
    try {
      const blob = await fetchBlob(url);
      return { blob, outputUrl: url };
    } catch (error) {
      lastError = error;
    }
  }
  throw lastError instanceof Error
    ? lastError
    : new Error("Failed to fetch project image");
}

export async function getOrFetch(
  requestId: string,
  outputUrl: string,
  fallbackUrls?: string[],
): Promise<{ blobUrl: string; revoke: () => void }> {
  const cached = await readEntry(requestId);
  if (cached) {
    const blobUrl = URL.createObjectURL(cached.blob);
    return { blobUrl, revoke: () => URL.revokeObjectURL(blobUrl) };
  }

  let pending = inflight.get(requestId);
  if (!pending) {
    pending = (async () => {
      try {
        const result = await fetchWithFallbacks(outputUrl, fallbackUrls || []);
        await writeEntry({
          requestId,
          blob: result.blob,
          cachedAt: Date.now(),
          outputUrl: result.outputUrl,
        });
        return result;
      } finally {
        inflight.delete(requestId);
      }
    })();
    inflight.set(requestId, pending);
  }

  const { blob } = await pending;
  const blobUrl = URL.createObjectURL(blob);
  return { blobUrl, revoke: () => URL.revokeObjectURL(blobUrl) };
}

export async function evict(requestId: string): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const { store, done } = tx(db, "readwrite");
    store.delete(requestId);
    await done;
  } catch (error) {
    console.warn("[projectImageCache] evict failed:", error);
  }
}

export async function evictAll(): Promise<void> {
  const db = await openDb();
  if (!db) return;
  try {
    const { store, done } = tx(db, "readwrite");
    store.clear();
    await done;
  } catch (error) {
    console.warn("[projectImageCache] evictAll failed:", error);
  }
}
